import type { PaperProps } from '@mui/material';
import { Box, Button, Divider, IconButton, Paper, Typography } from '@mui/material';
import { EditIcon } from 'apps/app/src/components/icons';
import { useIsLoadingMutation } from 'apps/app/src/hooks/use-is-loading-mutation';
import cn from 'classnames';
import React from 'react';
import type { FieldPath, FieldValue, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

type ConfigurationViewPropertiesBlockProps<FormType extends FieldValues> = PaperProps & {
  title: React.ReactNode,
  field: FieldPath<FormType>;
  fieldValue: FieldValue<FormType>,
  children: React.ReactNode;
  className?: string,
  childrenContainerClassName?: string,
  titleClassName?: string,
  showEditButton?: boolean,
  disableSaveButton?: boolean,
  onCancel?: () => void,
  onSubmit?: () => void,
};

export function ConfigurationViewPropertiesBlock<FormType extends FieldValues>({
  title,
  field,
  fieldValue,
  children,
  className: externalClassName,
  childrenContainerClassName: externalChildrenContainerClassName,
  titleClassName: externalTitleClassName,
  showEditButton = false,
  disableSaveButton = false,
  onCancel,
  onSubmit,
}: ConfigurationViewPropertiesBlockProps<FormType>) {
  const isUpdating = useIsLoadingMutation('update-integration-configuration');
  const { setValue, reset, watch, resetField } = useFormContext<FormType>();

  const isInEditMode = watch(field) === fieldValue;

  const handleEditClick = () => {
    reset();
    setValue(field, fieldValue);
  };

  const handleCancelClick = () => {
    resetField(field);
    reset();
    onCancel?.();
  };

  return (
    <Paper
      variant='outlined'
      className={twMerge('flex flex-col py-9', externalClassName)}
    >
      <div className='flex items-center justify-between px-6'>
        <Typography
          variant='h2'
          className={twMerge('text-xl font-medium', externalTitleClassName)}
        >
          {title}
        </Typography>
        <Box className={cn(!showEditButton && 'invisible', 'min-h-[36px]')}>
          {!isInEditMode ? (
            <IconButton
              onClick={handleEditClick}
            >
              <EditIcon className='fill-icon'/>
            </IconButton>
          ) : (<>
            <Button
              variant='text'
              className='px-2 py-0 text-base disabled:cursor-default disabled:opacity-50'
              disabled={isUpdating}
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className='px-2 py-0 text-base disabled:cursor-default disabled:opacity-50'
              disabled={isUpdating || disableSaveButton}
              onClick={onSubmit}
            >
              Save
            </Button>
          </>
          )}
        </Box>

      </div>
      <Divider className='my-7'/>
      <div className={twMerge('flex flex-wrap justify-start', externalChildrenContainerClassName)}>
        {children}
      </div>
    </Paper>
  );
}